import { groups } from '../presentation/catalogue';
import type { PresentationContext } from '../presentation/types';
import { brandLogoSvg } from './brandLogo';

/**
 * The MINT family as a tree: MINT at the root, one branch per enabled specialisation, and unnamed
 * branches for directions that do not exist yet. Shared by the specialisation reveal and the
 * "one foundation, many directions" return so the family always looks the same.
 */
export const ROOT = { x: 800, y: 250, r: 74 };
const ROW_Y = 590;

export interface TreeBranch {
  id: string;
  name: string;
  tagline: string;
  x: number;
  y: number;
}

export interface GhostBranch {
  x: number;
  y: number;
}

export function namedBranches(context: PresentationContext): TreeBranch[] {
  const list = context.products.filter((p) => p.tier === 'specialisation');
  const spacing = list.length > 1 ? Math.min(400, 1100 / (list.length - 1)) : 0;
  return list.map((p, i) => ({
    id: p.id,
    name: p.name,
    tagline: p.tagline,
    x: ROOT.x + (i - (list.length - 1) / 2) * spacing,
    y: ROW_Y,
  }));
}

/** Directions that are deliberately left unnamed (Prompt2 §4, §9). */
export const GHOSTS: Record<'one' | 'many', GhostBranch[]> = {
  // Kept above the named row so an unnamed direction never crosses a named branch.
  one: [{ x: 1330, y: 390 }],
  many: [{ x: 1330, y: 390 }, { x: 270, y: 390 }, { x: 1450, y: 170 }, { x: 150, y: 170 }],
};

const branchPath = (x: number, y: number): string =>
  `M${ROOT.x} ${ROOT.y + ROOT.r} C${ROOT.x} ${ROOT.y + 190} ${x} ${y - 190} ${x} ${y - 44}`;

const ghostPath = (g: GhostBranch): string => {
  const angle = Math.atan2(g.y - ROOT.y, g.x - ROOT.x);
  const sx = ROOT.x + Math.cos(angle) * (ROOT.r + 16);
  const sy = ROOT.y + Math.sin(angle) * (ROOT.r + 16);
  return `M${sx.toFixed(1)} ${sy.toFixed(1)} Q${((sx + g.x) / 2).toFixed(1)} ${(Math.max(sy, g.y) - 40).toFixed(1)} ${g.x} ${g.y}`;
};

/** Splits a tagline into two balanced lines so neighbouring branches never collide. */
function twoLines(text: string): [string, string] {
  if (text.length < 34) return [text, ''];
  const mid = text.length / 2;
  let cut = text.indexOf(' ', mid);
  const before = text.lastIndexOf(' ', mid);
  if (cut === -1 || (before !== -1 && mid - before < cut - mid)) cut = before;
  return [text.slice(0, cut), text.slice(cut + 1)];
}

/**
 * Renders the tree for a 1600×900 viewBox. `uid` keeps gradient ids unique per scene. Named branches carry `data-from` = `branchFrom`,
 * ghost branches carry `data-from` = `ghostFrom` (omit either to render it permanently).
 */
export function renderMintTree(
  context: PresentationContext,
  { uid, ghosts = 'one', branchFrom, ghostFrom }: { uid: string; ghosts?: 'one' | 'many' | 'none'; branchFrom?: number; ghostFrom?: number },
): string {
  const from = (step?: number): string => (step === undefined ? '' : ` data-from="${step}"`);
  const branches = namedBranches(context).map((b, i) => {
    const [l1, l2] = twoLines(b.tagline);
    return `
      <g class="mt-branch" data-branch="${b.id}" data-index="${i}"${from(branchFrom)}>
        <path class="mt-branch__path draw" pathLength="1" d="${branchPath(b.x, b.y)}"/>
        <g transform="translate(${b.x.toFixed(1)} ${b.y})">
          <g class="mt-branch__node">
            <circle class="mt-branch__halo" r="40"/>
            <circle class="mt-branch__dot" r="12"/>
          </g>
          <g class="mt-branch__text">
            <text class="mt-branch__name" y="80" text-anchor="middle">${b.name}</text>
            <text class="mt-branch__tagline" y="110" text-anchor="middle">${l1}</text>
            ${l2 ? `<text class="mt-branch__tagline" y="132" text-anchor="middle">${l2}</text>` : ''}
          </g>
        </g>
      </g>`;
  }).join('');

  const ghostList = ghosts === 'none' ? [] : GHOSTS[ghosts];
  const ghostMarkup = ghostList.map((g, i) => `
    <g class="mt-ghost" data-index="${i}"${from(ghostFrom)}>
      <path class="mt-ghost__path draw" pathLength="1" d="${ghostPath(g)}"/>
      <circle class="mt-ghost__glow" cx="${g.x}" cy="${g.y}" r="34" fill="url(#${uid}-ghost-fill)"/>
      <circle class="mt-ghost__ring" cx="${g.x}" cy="${g.y}" r="12"/>
    </g>`).join('');

  return `
    <defs>
      <radialGradient id="${uid}-ghost-fill">
        <stop offset="0" stop-color="#ffffff" stop-opacity=".3"/>
        <stop offset=".5" stop-color="${groups.mint.color}" stop-opacity=".14"/>
        <stop offset="1" stop-color="${groups.mint.color}" stop-opacity="0"/>
      </radialGradient>
    </defs>
    ${ghostMarkup}
    ${branches}
    <g class="mt-root">
      <circle class="mt-root__glow" cx="${ROOT.x}" cy="${ROOT.y}" r="${ROOT.r + 40}"/>
      <circle class="mt-root__ring" cx="${ROOT.x}" cy="${ROOT.y}" r="${ROOT.r}"/>
      ${brandLogoSvg('mint', { x: ROOT.x, y: ROOT.y, height: 30, surface: 'stage', className: 'mt-root__label' })}
    </g>`;
}
